/**
 * Frontend Logger Utility
 *
 * Provides structured logging with development/production mode awareness.
 * In production, errors are sent to Sentry (when available).
 */

const isDev = import.meta.env.DEV

/**
 * Sentry integration (lazy-loaded if available)
 */
let Sentry: any = null

// Lazy-load Sentry when first error is logged (avoids top-level await)
const loadSentry = async (): Promise<void> => {
  if (Sentry !== null) return
  try {
    Sentry = await import('@sentry/react')
  } catch {
    // Sentry not installed - silently continue (logs still work in dev)
    Sentry = false // Mark as attempted so we don't retry
  }
}

interface LogData {
  [key: string]: unknown
}

/**
 * Frontend logger with Sentry integration
 *
 * @example
 * import { logger } from '@/lib/logger'
 *
 * // Development only
 * logger.debug('Component rendered', { props })
 *
 * // Info (breadcrumbs in Sentry)
 * logger.info('User action', { action: 'click' })
 *
 * // Warnings
 * logger.warn('Deprecated feature used', { feature })
 *
 * // Errors (sent to Sentry)
 * logger.error('API call failed', error, { endpoint })
 */
export const logger = {
  /**
   * Debug logging (development only)
   * Removed in production builds
   */
  debug: (message: string, data?: LogData) => {
    if (isDev) {
      console.debug(`[DEBUG] ${message}`, data || '')
    }
  },

  /**
   * Informational logging
   * Creates breadcrumbs in Sentry for error context
   */
  info: (message: string, data?: LogData) => {
    if (isDev) {
      console.info(`[INFO] ${message}`, data || '')
    }

    if (Sentry && typeof Sentry === 'object') {
      Sentry.addBreadcrumb({
        category: 'app',
        message,
        level: 'info',
        data,
      })
    }
  },

  /**
   * Warning logging
   * Logged to console and added as Sentry breadcrumb
   */
  warn: (message: string, data?: LogData) => {
    console.warn(`[WARN] ${message}`, data || '')

    if (Sentry && typeof Sentry === 'object') {
      Sentry.addBreadcrumb({
        category: 'app',
        message,
        level: 'warning',
        data,
      })
    }
  },

  /**
   * Error logging
   * Logged to console and sent to Sentry
   *
   * @param message - Human-readable error description
   * @param error - Error object or unknown error value
   * @param data - Additional context data
   */
  error: async (message: string, error?: Error | unknown, data?: LogData) => {
    console.error(`[ERROR] ${message}`, error, data || '')

    // Lazy-load Sentry if needed
    await loadSentry()

    if (!Sentry || typeof Sentry !== 'object') return

    if (error instanceof Error) {
      Sentry.captureException(error, {
        extra: { message, ...data },
      })
    } else {
      Sentry.captureMessage(message, {
        level: 'error',
        extra: { error, ...data },
      })
    }
  },
}

export default logger
